import React, { useEffect } from 'react';

import {
  MapContainer,
  TileLayer,
  useMap,
  CircleMarker,
  Tooltip
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import HeatmapOverlay from 'leaflet-heatmap';


// import { Stack } from 'react-bootstrap';

import { translateComponent } from '@/utils/tolgee/translateComponent';

export interface ILomavisMapWithHighlights1Data {
  id: string; // Unique identifier for the location
  locationName: string; // Human-readable name (e.g., city)
  lat: number; // Latitude
  long: number; // Longitude
  weight: number;
}
interface ILomavisMapWithHighlights1Props {
  /** The text to display inside the H1 heading */
  title: string;
  data: ILomavisMapWithHighlights1Data[];
  style?: React.CSSProperties;
}

/**
 * Atoms: LomavisMapWithHighlights
 */

const HeatmapLayer: React.FC<{ data: ILomavisMapWithHighlights1Data[] }> = ({
  data
}) => {
  const map = useMap();

  useEffect(() => {
    const rawMax = Math.max(...data.map((d) => d.weight));

    // Avoid divide-by-zero and normalize to 0–10 scale
    const scaledData = data.map((d) => ({
      lat: d.lat,
      lng: d.long,
      count: rawMax > 0 ? (d.weight / rawMax) * 10 : 0
    }));

    const testData = {
      max: 10, // should match your scaling
      data: scaledData
    };

    const cfg = {
      radius: 1, // balanced: adjust based on zoom
      maxOpacity: 0.8,
      scaleRadius: true,
      useLocalExtrema: false,
      latField: 'lat',
      lngField: 'lng',
      valueField: 'count'
    };

    const heatmapLayer = new HeatmapOverlay(cfg);
    heatmapLayer.setData(testData);
    heatmapLayer.addTo(map);

    // 🟢 Auto zoom to focus on the highest weighted location

    // Get only the highest weighted location
    const highestWeighted = [...data].sort((a, b) => b.weight - a.weight)[0];
    
    // Set a good zoom level for a single location
    const zoomLevel = 5; // City level zoom that provides good detail
    
    // Center the map on the highest weighted location with appropriate zoom
    map.setView([highestWeighted.lat, highestWeighted.long], zoomLevel);

    return () => {
      map.removeLayer(heatmapLayer);
    };
  }, [data, map]);

  return null;
};

export const LomavisMapWithHighlights1: React.FC<
  ILomavisMapWithHighlights1Props
> = ({ title, data, style }) => {
  const t = translateComponent("LomavisMapWithHighlights1");

  if (data.length === 0)
    return <div>{t('lomavisMapWithHighlights1.noData')}</div>;

  const center: [number, number] = [
    data.reduce((sum, d) => sum + d.lat, 0) / data.length,
    data.reduce((sum, d) => sum + d.long, 0) / data.length
  ];

  return (
    <div style={{ width: '100%', height: '100%', ...style }}>
      <MapContainer
        center={center}
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors & CartoDB"
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />

        <HeatmapLayer data={data} />
        {data.map((item) => (
          <CircleMarker
            key={item.id}
            center={[item.lat, item.long]}
            radius={8}
            color="transparent"
            fillColor="transparent"
            fillOpacity={0}
          >
            <Tooltip direction="top" offset={[0, -10]} opacity={1}>
              <span>
                {item.locationName}: {item.weight}
              </span>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
};
